import { FC, MutableRefObject, useEffect, useRef, useState } from 'react'

import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs'
import { styled } from 'styled-components'

import IconButton from 'core/components/lib/IconButton'
import { Select } from 'core/components/lib/Select'
import TextInput from 'core/components/lib/TextInput'
import { R, assert } from 'core/helpers'
import { onSurface, onSurfaceSecondary } from 'core/styles'

import FindPopover from './FindPopover'
import PrintButton from './PrintButton'
import ThumbnailsButton from './ThumbnailsButton'

type ToolbarProps = {
  pdfViewer: PDFViewer
  filename: string
}

const Toolbar: FC<ToolbarProps> = ({ pdfViewer, filename }) => {
  const pageNumberRef = useRef() as MutableRefObject<HTMLInputElement>

  const [pageNumber, setPageNumber] = useState(0)
  const [scale, setScale] = useState<null | number | string>(null)

  const [pageNumberValue, setPageNumberValue] = useState('')

  useEffect(() => {
    pdfViewer.currentScaleValue = 'auto'

    pdfViewer.eventBus.on('updateviewarea', (data) => {
      setPageNumber(data.location.pageNumber)
      setPageNumberValue(data.location.pageNumber.toString())
    })
    pdfViewer.eventBus.on('scalechanging', (data) => setScale(data.presetValue || data.scale))
  }, [pdfViewer])

  const scrollPageIntoView = (page: number) =>
    pdfViewer.scrollPageIntoView({ pageNumber: Math.max(1, Math.min(page, pdfViewer.pagesCount)) })

  return (
    <Box>
      <ThumbnailsButton onClick={scrollPageIntoView} pageNumber={pageNumber} pdfViewer={pdfViewer} />
      <FindPopover eventBus={pdfViewer.eventBus} />

      <Spacer />

      <IconButton
        color='inherit'
        disabled={pageNumber <= 1}
        fontSize='18px'
        name='keyboard_arrow_up'
        onClick={() => {
          pdfViewer.previousPage()
        }}
      />
      <IconButton
        color='inherit'
        disabled={pageNumber >= pdfViewer.pagesCount}
        fontSize='18px'
        name='keyboard_arrow_down'
        onClick={() => {
          pdfViewer.nextPage()
        }}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          scrollPageIntoView(Number.parseInt(pageNumberValue))
        }}
      >
        <StyledTextInput
          format='integer'
          onBlur={() => scrollPageIntoView(Number.parseInt(pageNumberValue))}
          onChange={setPageNumberValue}
          onFocus={() => pageNumberRef.current.select()}
          onKeyDown={(e) => {
            switch (e.key) {
              case 'ArrowDown':
                pdfViewer.nextPage()
                break
              case 'ArrowUp':
                pdfViewer.previousPage()
                break
              default:
                return
            }
          }}
          ref={pageNumberRef}
          value={pageNumberValue}
          variant='underline'
        />
        <span> of {pdfViewer.pagesCount}</span>
      </form>

      <Spacer />

      <IconButton
        color='inherit'
        disabled={R.isNumber(scale) && scale <= 0.25}
        fontSize='18px'
        name='remove'
        onClick={() => pdfViewer.decreaseScale()}
      />
      <SelectWrapper>
        <Select
          classNamePrefix='inline'
          onChange={(value) => {
            if (R.isNumber(value)) {
              pdfViewer.currentScale = value
            } else if (value) {
              pdfViewer.currentScaleValue = value
            }
          }}
          options={R.compact([
            { value: 'auto', label: 'Automatic' },
            { value: 'page-actual', label: 'Actual Size' },
            { value: 'page-fit', label: 'Page Fit' },
            { value: 'page-width', label: 'Page Width' },
            { value: 0.5, label: '50%' },
            { value: 0.75, label: '75%' },
            { value: 1, label: '100%' },
            { value: 1.25, label: '125%' },
            { value: 1.5, label: '150%' },
            { value: 2, label: '200%' },
            R.isNumber(scale) && { value: scale, label: `${Math.round(scale * 100)}%` },
          ])}
          value={scale}
        />
      </SelectWrapper>
      <IconButton
        color='inherit'
        disabled={R.isNumber(scale) && scale >= 5}
        fontSize='18px'
        name='add'
        onClick={() => pdfViewer.increaseScale()}
      />

      <Spacer />

      <IconButton
        color='inherit'
        fontSize='18px'
        name='rotate_right'
        onClick={() => {
          pdfViewer.pagesRotation += 90
        }}
      />
      <IconButton
        color='inherit'
        fontSize='18px'
        name='rotate_left'
        onClick={() => {
          pdfViewer.pagesRotation -= 90
        }}
      />
      <IconButton
        color='inherit'
        fontSize='18px'
        name='download'
        onClick={async () => {
          assert(pdfViewer.pdfDocument, 'No PDF document loaded')
          pdfViewer.downloadManager?.downloadData(await pdfViewer.pdfDocument.getData(), filename)
        }}
      />
      <PrintButton pdfViewer={pdfViewer} />
    </Box>
  )
}

export default Toolbar

const Box = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  background-color: ${onSurfaceSecondary};
  padding: 0 16px;
  color: white;
  font-size: 14px;
`

const Spacer = styled.div`
  flex-grow: 1;
`

const SelectWrapper = styled.div`
  width: 120px;

  input {
    color: white !important;
  }

  div:not(:has(> input)) {
    background-color: ${onSurfaceSecondary};
    color: white;
  }

  div[role='option']:hover,
  div[role='option']:hover div {
    background-color: ${onSurface};
  }
`

const StyledTextInput = styled(TextInput)`
  width: 32px;
  text-align: center;
`
